import { useEffect } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import { MapPinned } from "lucide-react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { categoryById } from "../data/categories";
import type { Place } from "../data/schema";
import { priceLabels, stayTimeLabels } from "../lib/labels";

interface MapViewProps {
  places: Place[];
  selectedPlace?: Place;
  onSelect: (place: Place) => void;
}

export function MapView({ places, selectedPlace, onSelect }: MapViewProps) {
  return (
    <section className="map-shell" aria-label="전국 아이와 갈 만한 곳 지도">
      <div className="map-header">
        <div>
          <MapPinned size={20} aria-hidden="true" />
          <div>
            <h2>지도 탐색</h2>
            <p>{selectedPlace ? selectedPlace.name : "선택 지역 전체"}</p>
          </div>
        </div>
        <span>{places.length}곳</span>
      </div>

      <div className="map-frame">
        <MapContainer
          center={[36.3, 127.8]}
          zoom={7}
          minZoom={6}
          maxZoom={18}
          scrollWheelZoom
          className="map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClusteredMarkers
            onSelect={onSelect}
            places={places}
            selectedPlaceId={selectedPlace?.id}
          />
          <VisiblePlacesFitBounds places={places} selectedPlace={selectedPlace} />
          <SelectedPlaceFlyTo place={selectedPlace} />
        </MapContainer>
      </div>
    </section>
  );
}

function VisiblePlacesFitBounds({ places, selectedPlace }: { places: Place[]; selectedPlace?: Place }) {
  const map = useMap();

  useEffect(() => {
    if (selectedPlace || places.length === 0) return;
    if (places.length === 1) {
      map.setView([places[0].coordinates.lat, places[0].coordinates.lng], 13);
      return;
    }

    const bounds = L.latLngBounds(
      places.map((place) => [place.coordinates.lat, place.coordinates.lng] as [number, number]),
    );
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [map, places, selectedPlace]);

  return null;
}

function ClusteredMarkers({
  places,
  selectedPlaceId,
  onSelect,
}: {
  places: Place[];
  selectedPlaceId?: string;
  onSelect: (place: Place) => void;
}) {
  const map = useMap();

  useEffect(() => {
    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 48,
    });

    places.forEach((place) => {
      const marker = L.marker([place.coordinates.lat, place.coordinates.lng], {
        icon: createMarkerIcon(place, selectedPlaceId === place.id),
      });

      marker.bindPopup(renderPopup(place));
      marker.on("click", () => onSelect(place));
      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);

    return () => {
      map.removeLayer(clusterGroup);
    };
  }, [map, onSelect, places, selectedPlaceId]);

  return null;
}

function SelectedPlaceFlyTo({ place }: { place?: Place }) {
  const map = useMap();

  useEffect(() => {
    if (!place) return;
    map.flyTo([place.coordinates.lat, place.coordinates.lng], Math.max(map.getZoom(), 13), {
      duration: 0.7,
    });
  }, [map, place]);

  return null;
}

function createMarkerIcon(place: Place, selected: boolean) {
  const category = categoryById[place.category];
  const size = selected ? 38 : 31;
  const className = selected ? "custom-marker selected" : "custom-marker";
  const html = `<span style="background:${category.color}">${category.shortName}</span>`;

  return L.divIcon({
    className,
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

function renderPopup(place: Place) {
  return `
    <div class="map-popup">
      <strong>${escapeHtml(place.name)}</strong>
      <span>${escapeHtml(categoryById[place.category].name)}</span>
      <p>${escapeHtml(place.summary)}</p>
      <small>${escapeHtml(place.district)} · ${escapeHtml(
        priceLabels[place.priceType],
      )} · ${escapeHtml(stayTimeLabels[place.stayTime])}</small>
    </div>
  `;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
